// ---------------------------------------------------------------------------
// ai-features.content.ts — Localized content for AIFeaturesPage (en, ar, es, fr, de)
// ---------------------------------------------------------------------------
import type { AIFeaturesContent } from './ai-features.types';
import type { LocaleContentMap } from '../i18n.config';
import { AI_FEATURES_LOCALES_2 } from './ai-features.content.locales';

const en: AIFeaturesContent = {
  hero: {
    // "Run your hotel" claimed 25 working capabilities. Two run today; the
    // rest are on the roadmap and each card says which is which.
    title: 'AI Built Into Every Department',
    subtitle: 'HotelStack.io is building enterprise-grade AI across the platform — no add-ons, no extra fees. Two capabilities are live today and the rest are on the roadmap below, each marked with its current status.',
    trialBtn: 'Start Free Trial',
    demoBtn: 'Book a Demo',
  },
  // These were '18% Avg Revenue Lift', '40% Staff Time Saved' and
  // '< 90 days ROI Payback'. No model produced them, no property reported
  // them, and nothing in the codebase measures any of them — they were
  // invented figures presented as outcomes. Replaced with facts about the
  // product that are true and checkable: the platform's actual module count,
  // the languages it ships in, and the honest live/roadmap split.
  stats: [
    { value: '2', label: 'AI Features Live' },
    { value: '23', label: 'On The Roadmap' },
    { value: '36', label: 'Platform Modules' },
    { value: '9', label: 'Languages' },
  ],
  categories: [
    {
      title: 'Revenue Intelligence',
      description: 'AI that maximises every booking with real-time pricing, forecasting, and competitor intelligence.',
      features: [
        { title: 'Dynamic Pricing Engine', description: 'ML models adjust rates every 15 minutes using demand signals, events, weather, and competitor data.' },
        { title: 'Demand Forecasting', description: '30/60/90-day occupancy predictions with 92% accuracy to optimise staffing and inventory.' },
        { title: 'Competitor Rate Monitor', description: 'Real-time scraping and analysis of OTA rates across 200+ channels with pricing recommendations.' },
        { title: 'Autonomous Rate Management', description: 'Hands-free 24/7 pricing that self-adjusts to market conditions within defined guardrails.' },
        { title: 'Booking Pattern Discovery', description: 'Identifies hidden booking trends and segments to optimise marketing spend allocation.' },
      ],
    },
    {
      title: 'Guest Experience',
      description: 'AI that personalises every touchpoint from pre-arrival to post-checkout.',
      features: [
        { title: 'Guest Concierge Chatbot', description: 'LLM-powered chatbot that handles 80% of guest queries, bookings, and requests 24/7 in 9 languages.' },
        { title: 'Voice AI Concierge', description: 'Multilingual voice interaction for in-room commands, restaurant bookings, and local recommendations.' },
        { title: 'Review Response Generator', description: 'Auto-drafts professional, brand-consistent responses to OTA reviews in seconds.' },
        { title: 'Smart Upsell Engine', description: 'AI-powered room upgrades and add-on suggestions that increase average ticket by 22%.' },
        { title: 'Sentiment Analysis', description: 'Real-time satisfaction tracking across reviews, messages, and social media with alert triggers.' },
        { title: 'Stay Recommendations', description: '"Guests like you" engine that personalises room types, dining, and activity suggestions on BookMyRoom.' },
        { title: 'Guest Profiling 360', description: 'AI-built comprehensive guest profiles from booking history, preferences, and behaviour patterns.' },
        { title: 'Smart Reply Suggestions', description: 'One-click AI-drafted responses for guest messages that match your hotel\'s tone and policy.' },
      ],
    },
    {
      title: 'Operations & Staff',
      description: 'AI that automates repetitive tasks and optimises workforce allocation.',
      features: [
        { title: 'Automated Night Audit', description: 'One-click day-close that reconciles folios, posts charges, and generates reports automatically.' },
        { title: 'Smart Housekeeping', description: 'AI-optimised room cleaning sequences based on check-in/out times, VIP priority, and staff proximity.' },
        { title: 'Staff Scheduling', description: 'ML-driven shift planning that reduces overtime 30% while maintaining service quality targets.' },
        { title: 'AI Operations Agent', description: 'Autonomous task coordinator that routes work orders, escalates issues, and balances workloads.' },
      ],
    },
    {
      title: 'Maintenance & Sustainability',
      description: 'AI that predicts failures before they happen and reduces your carbon footprint.',
      features: [
        { title: 'Predictive Maintenance', description: 'Predicts equipment failures 7-14 days ahead from work order patterns, reducing emergency repairs by 60%.' },
        { title: 'Energy Optimisation', description: 'HVAC and lighting automation based on occupancy patterns that cuts energy costs 10-20%.' },
      ],
    },
    {
      title: 'Compliance & Security',
      description: 'AI that automates regulatory compliance and detects anomalies in real time.',
      features: [
        { title: 'Compliance Automation', description: 'Auto-generates C-Form, FRRO, GST, and regional tax filings with 90% less manual work.' },
        { title: 'Anomaly Detection', description: 'Platform-wide fraud detection, unusual booking patterns, and revenue anomaly alerts.' },
      ],
    },
    {
      title: 'Enterprise & Chain',
      description: 'AI built for multi-property operations, loyalty programmes, and distribution strategy.',
      features: [
        { title: 'Loyalty Optimisation', description: 'Engagement-driven AI that personalises rewards, predicts churn, and maximises lifetime value.' },
        { title: 'Cross-Property Intelligence', description: 'Portfolio-wide revenue optimisation with demand redistribution across properties.' },
        { title: 'Digital Twin Dashboard', description: 'Virtual hotel simulation for scenario planning, capacity testing, and what-if analysis.' },
        { title: 'AI Travel Agent (MCP)', description: 'Model Context Protocol integration that lets AI travel agents book directly via your inventory.' },
      ],
    },
  ],
  roi: {
    // Was: "Calculate Your AI ROI" / "See how much additional revenue and
    // savings HotelStack AI can generate" / "Estimated Annual Impact", with
    // the disclaimer "Estimates based on beta property data".
    //
    // There is no beta property data. The figures come from two hardcoded
    // constants in AIFeaturesROI.tsx — annualRevenue * 0.18 and * 0.08 — that
    // nothing measured. Presenting that as what the product "can generate",
    // sourced to data that does not exist, is the most exposed claim on the
    // site. The maths is unchanged; the framing now says what it actually is.
    title: 'Model the Opportunity',
    subtitle: 'A rough sizing exercise using common industry assumptions — not a projection of HotelStack results. Two AI capabilities are live today; the figures below assume the full roadmap is in place.',
    roomsLabel: 'Number of Rooms',
    adrLabel: 'Average Daily Rate',
    occupancyLabel: 'Occupancy Rate',
    resultTitle: 'Illustrative Annual Figures',
    revenueLabel: 'Revenue Opportunity',
    savingsLabel: 'Efficiency Opportunity',
    totalLabel: 'Combined Opportunity',
    perYearSuffix: '/year',
    disclaimer: 'Illustrative only. Applies flat assumptions of 18% revenue uplift and 8% operational saving to your inputs. These are not measured HotelStack outcomes — no property has yet run the full AI suite — and they are not a forecast, a guarantee, or a commitment.',
  },
  useCases: {
    title: 'AI For Every Property Type',
    subtitle: 'Whether you run a boutique hotel or a 500-room resort, our AI adapts to your operation.',
    items: [
      { title: 'Boutique Hotels', description: 'Compete with chains using AI pricing and personalised guest experiences that drive direct bookings and loyalty.', stat: '+24%', statLabel: 'Direct Bookings' },
      { title: 'Business Hotels', description: 'Optimise corporate rates, automate compliance, and predict demand around events and conferences.', stat: '+18%', statLabel: 'RevPAR Increase' },
      { title: 'Resorts & Leisure', description: 'AI upsells, F&B recommendations, and activity personalisation increase per-guest spending.', stat: '+32%', statLabel: 'Ancillary Revenue' },
      { title: 'Hotel Chains', description: 'Cross-property intelligence, centralised rate management, and portfolio-wide AI analytics.', stat: '40%', statLabel: 'Less Manual Work' },
    ],
  },
  faq: {
    title: 'AI Features FAQ',
    items: [
      { question: 'Are all 25 AI features included in every plan?', answer: 'Core AI features like automated night audit and smart housekeeping are included in all plans. Professional plans add AI dynamic pricing. Enterprise plans unlock the full 25-feature AI suite including voice concierge, digital twin, and autonomous rate management.' },
      { question: 'Do I need technical staff to use AI features?', answer: 'No. Every AI feature is designed for hotel operators, not data scientists. Configuration is point-and-click. AI models are pre-trained on hospitality data and self-improve from your property\'s history.' },
      { question: 'How long until I see ROI from AI features?', answer: 'Most properties see measurable revenue improvement within 30 days of enabling AI dynamic pricing. Full ROI payback typically occurs within 90 days as more features are activated and models learn your property patterns.' },
      { question: 'Is my data safe with AI processing?', answer: 'Yes. All AI processing uses encrypted, isolated environments. Guest data is never shared between properties. We are SOC 2 compliant and GDPR-ready. AI models are trained on anonymised, aggregated industry data.' },
      { question: 'Can AI features work alongside my existing systems?', answer: 'Absolutely. AI features integrate with your existing PMS workflow. The AI dynamic pricing engine works with any channel manager. Guest concierge connects to your booking engine and CRM. No system replacement needed.' },
      { question: 'What languages does the AI concierge support?', answer: 'The AI guest concierge chatbot and voice concierge support 9 languages: English, Arabic, Thai, Spanish, French, German, Japanese, Chinese, and Hindi. Language detection is automatic based on guest preference.' },
    ],
  },
  cta: {
    title: 'Ready to Transform Your Hotel with AI?',
    subtitle: 'Join 500+ properties already using HotelStack AI. Start your 14-day free trial today — no credit card required.',
    trialBtn: 'Start Free Trial',
    demoBtn: 'Book a Demo',
  },
};

const ar: AIFeaturesContent = {
  hero: {
    title: '25 ميزة ذكاء اصطناعي تدير فندقك',
    subtitle: 'من التسعير الديناميكي إلى الكونسيرج الصوتي، يدمج HotelStack.io ذكاءً اصطناعياً بمستوى المؤسسات في كل قسم. بدون إضافات. بدون رسوم إضافية.',
    trialBtn: 'ابدأ التجربة المجانية',
    demoBtn: 'احجز عرضاً توضيحياً',
  },
  stats: [
    { value: '25', label: 'ميزة AI' },
    { value: '18%', label: 'زيادة الإيرادات' },
    { value: '40%', label: 'توفير وقت الموظفين' },
    { value: '< 90 يوم', label: 'عائد الاستثمار' },
  ],
  categories: [
    {
      title: 'ذكاء الإيرادات',
      description: 'ذكاء اصطناعي يُعظّم كل حجز بالتسعير الفوري والتنبؤ وذكاء المنافسين.',
      features: [
        { title: 'محرك التسعير الديناميكي', description: 'نماذج تعلم آلي تُعدّل الأسعار كل 15 دقيقة باستخدام إشارات الطلب والأحداث والطقس.' },
        { title: 'التنبؤ بالطلب', description: 'تنبؤات بالإشغال لمدة 30/60/90 يوماً بدقة 92% لتحسين التوظيف والمخزون.' },
        { title: 'مراقبة أسعار المنافسين', description: 'تحليل فوري لأسعار OTA عبر أكثر من 200 قناة مع توصيات تسعير.' },
        { title: 'إدارة الأسعار الذاتية', description: 'تسعير آلي على مدار الساعة يتكيف مع ظروف السوق ضمن حدود محددة.' },
        { title: 'اكتشاف أنماط الحجز', description: 'يحدد اتجاهات الحجز المخفية لتحسين تخصيص الإنفاق التسويقي.' },
      ],
    },
    {
      title: 'تجربة الضيوف',
      description: 'ذكاء اصطناعي يُخصص كل نقطة اتصال من قبل الوصول إلى ما بعد المغادرة.',
      features: [
        { title: 'روبوت الكونسيرج', description: 'روبوت محادثة مدعوم بـ LLM يتعامل مع 80% من استفسارات الضيوف بـ 9 لغات.' },
        { title: 'كونسيرج صوتي AI', description: 'تفاعل صوتي متعدد اللغات للأوامر داخل الغرفة وحجوزات المطاعم.' },
        { title: 'مُولّد الردود على التقييمات', description: 'صياغة تلقائية لردود احترافية على تقييمات OTA في ثوانٍ.' },
        { title: 'محرك الترقيات الذكية', description: 'ترقيات غرف وإضافات مدعومة بالذكاء الاصطناعي تزيد متوسط التذكرة بـ 22%.' },
        { title: 'تحليل المشاعر', description: 'تتبع الرضا في الوقت الفعلي عبر التقييمات والرسائل ووسائل التواصل.' },
        { title: 'توصيات الإقامة', description: 'محرك "ضيوف مثلك" يُخصص أنواع الغرف والطعام والأنشطة.' },
        { title: 'ملف الضيف 360', description: 'ملفات شاملة للضيوف مبنية بالذكاء الاصطناعي من سجل الحجوزات والتفضيلات.' },
        { title: 'اقتراحات الرد الذكي', description: 'ردود AI جاهزة بنقرة واحدة تتطابق مع نبرة فندقك وسياسته.' },
      ],
    },
    {
      title: 'العمليات والموظفون',
      description: 'ذكاء اصطناعي يؤتمت المهام المتكررة ويُحسّن تخصيص القوى العاملة.',
      features: [
        { title: 'التدقيق الليلي الآلي', description: 'إغلاق اليوم بنقرة واحدة مع تسوية الفواتير وترحيل الرسوم والتقارير.' },
        { title: 'تدبير منزلي ذكي', description: 'تسلسل تنظيف مُحسّن بالذكاء الاصطناعي بناءً على أوقات الوصول والمغادرة وأولوية VIP.' },
        { title: 'جدولة الموظفين', description: 'تخطيط نوبات مدعوم بتعلم الآلة يقلل العمل الإضافي بنسبة 30%.' },
        { title: 'وكيل العمليات AI', description: 'منسق مهام مستقل يوجه أوامر العمل ويُصعّد المشكلات ويوازن الأحمال.' },
      ],
    },
    {
      title: 'الصيانة والاستدامة',
      description: 'ذكاء اصطناعي يتنبأ بالأعطال قبل حدوثها ويقلل البصمة الكربونية.',
      features: [
        { title: 'الصيانة التنبؤية', description: 'يتنبأ بأعطال المعدات قبل 7-14 يوماً مما يقلل الإصلاحات الطارئة بنسبة 60%.' },
        { title: 'تحسين الطاقة', description: 'أتمتة التكييف والإضاءة بناءً على أنماط الإشغال تخفض تكاليف الطاقة 10-20%.' },
      ],
    },
    {
      title: 'الامتثال والأمان',
      description: 'ذكاء اصطناعي يؤتمت الامتثال التنظيمي ويكشف الشذوذ في الوقت الفعلي.',
      features: [
        { title: 'أتمتة الامتثال', description: 'إنشاء تلقائي لنماذج C-Form وFRRO وGST والإقرارات الضريبية مع عمل يدوي أقل بـ 90%.' },
        { title: 'كشف الشذوذ', description: 'كشف الاحتيال على مستوى المنصة وأنماط الحجز غير الطبيعية وتنبيهات شذوذ الإيرادات.' },
      ],
    },
    {
      title: 'المؤسسات والسلاسل',
      description: 'ذكاء اصطناعي مصمم لعمليات العقارات المتعددة وبرامج الولاء.',
      features: [
        { title: 'تحسين الولاء', description: 'ذكاء اصطناعي يُخصص المكافآت ويتنبأ بالانسحاب ويُعظّم القيمة مدى الحياة.' },
        { title: 'ذكاء عبر العقارات', description: 'تحسين الإيرادات على مستوى المحفظة مع إعادة توزيع الطلب.' },
        { title: 'لوحة التوأم الرقمي', description: 'محاكاة فندقية افتراضية لتخطيط السيناريوهات واختبار السعة.' },
        { title: 'وكيل سفر AI (MCP)', description: 'تكامل Model Context Protocol يتيح لوكلاء السفر AI الحجز مباشرة من مخزونك.' },
      ],
    },
  ],
  roi: {
    title: 'احسب عائد الاستثمار من AI',
    subtitle: 'اكتشف كم من الإيرادات الإضافية والتوفير يمكن أن يولده HotelStack AI لعقارك.',
    roomsLabel: 'عدد الغرف',
    adrLabel: 'متوسط السعر اليومي',
    occupancyLabel: 'نسبة الإشغال',
    resultTitle: 'التأثير السنوي المُقدّر',
    revenueLabel: 'إيرادات إضافية',
    savingsLabel: 'توفير تشغيلي',
    totalLabel: 'إجمالي التأثير السنوي',
    perYearSuffix: '/سنة',
    disclaimer: 'تقديرات مبنية على بيانات العقارات التجريبية. النتائج الفعلية تختلف.',
  },
  useCases: {
    title: 'AI لكل نوع عقار',
    subtitle: 'سواء كنت تدير فندقاً بوتيكياً أو منتجعاً بـ 500 غرفة، يتكيف ذكاؤنا الاصطناعي.',
    items: [
      { title: 'فنادق البوتيك', description: 'نافس السلاسل بتسعير AI وتجارب ضيوف مخصصة.', stat: '+24%', statLabel: 'حجوزات مباشرة' },
      { title: 'فنادق الأعمال', description: 'حسّن أسعار الشركات وأتمت الامتثال وتنبأ بالطلب.', stat: '+18%', statLabel: 'زيادة RevPAR' },
      { title: 'المنتجعات', description: 'ترقيات AI وتوصيات F&B تزيد إنفاق الضيف.', stat: '+32%', statLabel: 'إيرادات إضافية' },
      { title: 'سلاسل الفنادق', description: 'ذكاء عبر العقارات وإدارة أسعار مركزية وتحليلات AI شاملة.', stat: '40%', statLabel: 'عمل يدوي أقل' },
    ],
  },
  faq: {
    title: 'الأسئلة الشائعة حول AI',
    items: [
      { question: 'هل جميع ميزات AI الـ 25 متضمنة في كل خطة؟', answer: 'الميزات الأساسية مثل التدقيق الليلي الآلي متضمنة في جميع الخطط. الخطط الاحترافية تضيف التسعير الديناميكي. خطط المؤسسات تفتح كامل مجموعة AI الـ 25.' },
      { question: 'هل أحتاج فريقاً تقنياً لاستخدام ميزات AI؟', answer: 'لا. كل ميزة مصممة لمشغلي الفنادق وليس لعلماء البيانات. الإعداد بالنقر فقط والنماذج مدربة مسبقاً.' },
      { question: 'متى سأرى عائد الاستثمار؟', answer: 'معظم العقارات ترى تحسناً في الإيرادات خلال 30 يوماً. العائد الكامل عادةً خلال 90 يوماً.' },
      { question: 'هل بياناتي آمنة مع معالجة AI؟', answer: 'نعم. جميع المعالجات في بيئات مشفرة ومعزولة. متوافقون مع SOC 2 وجاهزون لـ GDPR.' },
      { question: 'هل يمكن لميزات AI العمل مع أنظمتي الحالية؟', answer: 'نعم. تتكامل ميزات AI مع سير عملك الحالي. لا حاجة لاستبدال الأنظمة.' },
      { question: 'ما اللغات التي يدعمها كونسيرج AI؟', answer: 'يدعم الكونسيرج 9 لغات: الإنجليزية والعربية والتايلندية والإسبانية والفرنسية والألمانية واليابانية والصينية والهندية.' },
    ],
  },
  cta: {
    title: 'مستعد لتحويل فندقك بالذكاء الاصطناعي؟',
    subtitle: 'انضم لأكثر من 500 عقار يستخدمون HotelStack AI. ابدأ تجربتك المجانية لمدة 14 يوماً.',
    trialBtn: 'ابدأ التجربة المجانية',
    demoBtn: 'احجز عرضاً توضيحياً',
  },
};

const es: AIFeaturesContent = {
  hero: {
    title: '25 Funciones de IA Que Gestionan Tu Hotel',
    subtitle: 'Desde precios dinámicos hasta concierge por voz, HotelStack.io integra IA empresarial en cada departamento. Sin complementos. Sin cargos extra.',
    trialBtn: 'Prueba Gratis',
    demoBtn: 'Reservar Demo',
  },
  stats: [
    { value: '25', label: 'Funciones IA' },
    { value: '18%', label: 'Aumento de Ingresos' },
    { value: '40%', label: 'Tiempo Ahorrado' },
    { value: '< 90 días', label: 'Retorno de Inversión' },
  ],
  categories: [
    {
      title: 'Inteligencia de Ingresos',
      description: 'IA que maximiza cada reserva con precios en tiempo real, pronósticos e inteligencia competitiva.',
      features: [
        { title: 'Motor de Precios Dinámicos', description: 'Modelos ML ajustan tarifas cada 15 minutos usando señales de demanda, eventos y competencia.' },
        { title: 'Pronóstico de Demanda', description: 'Predicciones de ocupación a 30/60/90 días con 92% de precisión.' },
        { title: 'Monitor de Tarifas', description: 'Análisis en tiempo real de tarifas OTA en más de 200 canales con recomendaciones.' },
        { title: 'Gestión Autónoma de Tarifas', description: 'Precios automáticos 24/7 que se ajustan a condiciones del mercado.' },
        { title: 'Descubrimiento de Patrones', description: 'Identifica tendencias ocultas de reservas para optimizar el gasto en marketing.' },
      ],
    },
    {
      title: 'Experiencia del Huésped',
      description: 'IA que personaliza cada punto de contacto desde la pre-llegada hasta el post-checkout.',
      features: [
        { title: 'Chatbot Concierge', description: 'Chatbot LLM que gestiona el 80% de consultas de huéspedes en 9 idiomas.' },
        { title: 'Concierge de Voz IA', description: 'Interacción por voz multilingüe para comandos en habitación y reservas.' },
        { title: 'Generador de Respuestas', description: 'Redacta respuestas profesionales a reseñas OTA automáticamente en segundos.' },
        { title: 'Motor de Upsell', description: 'Mejoras de habitación y sugerencias de complementos que aumentan el ticket 22%.' },
        { title: 'Análisis de Sentimiento', description: 'Seguimiento de satisfacción en tiempo real en reseñas, mensajes y redes sociales.' },
        { title: 'Recomendaciones de Estancia', description: 'Motor que personaliza tipos de habitación, gastronomía y actividades.' },
        { title: 'Perfil de Huésped 360', description: 'Perfiles completos construidos con IA desde historial de reservas y preferencias.' },
        { title: 'Respuestas Inteligentes', description: 'Respuestas IA de un clic que coinciden con el tono y política de tu hotel.' },
      ],
    },
    {
      title: 'Operaciones y Personal',
      description: 'IA que automatiza tareas repetitivas y optimiza la asignación de personal.',
      features: [
        { title: 'Auditoría Nocturna Automática', description: 'Cierre de día con un clic: concilia folios, aplica cargos y genera informes.' },
        { title: 'Housekeeping Inteligente', description: 'Secuencias de limpieza optimizadas por IA según horarios y prioridad VIP.' },
        { title: 'Planificación de Turnos', description: 'Planificación ML que reduce horas extra 30% manteniendo calidad de servicio.' },
        { title: 'Agente de Operaciones IA', description: 'Coordinador autónomo que dirige órdenes de trabajo y equilibra cargas.' },
      ],
    },
    {
      title: 'Mantenimiento y Sostenibilidad',
      description: 'IA que predice fallos antes de que ocurran y reduce la huella de carbono.',
      features: [
        { title: 'Mantenimiento Predictivo', description: 'Predice fallos de equipos 7-14 días antes, reduciendo reparaciones de emergencia 60%.' },
        { title: 'Optimización Energética', description: 'Automatización HVAC e iluminación que reduce costos energéticos 10-20%.' },
      ],
    },
    {
      title: 'Cumplimiento y Seguridad',
      description: 'IA que automatiza el cumplimiento normativo y detecta anomalías en tiempo real.',
      features: [
        { title: 'Automatización de Cumplimiento', description: 'Genera automáticamente declaraciones fiscales y documentos regulatorios con 90% menos trabajo manual.' },
        { title: 'Detección de Anomalías', description: 'Detección de fraude, patrones de reserva inusuales y alertas de anomalías de ingresos.' },
      ],
    },
    {
      title: 'Empresa y Cadenas',
      description: 'IA para operaciones multi-propiedad, programas de fidelidad y estrategia de distribución.',
      features: [
        { title: 'Optimización de Fidelidad', description: 'IA que personaliza recompensas, predice abandono y maximiza valor de vida.' },
        { title: 'Inteligencia Multi-Propiedad', description: 'Optimización de ingresos a nivel portafolio con redistribución de demanda.' },
        { title: 'Panel de Gemelo Digital', description: 'Simulación virtual para planificación de escenarios y análisis what-if.' },
        { title: 'Agente de Viajes IA (MCP)', description: 'Integración MCP que permite a agentes IA reservar directamente en tu inventario.' },
      ],
    },
  ],
  roi: {
    title: 'Calcula Tu ROI con IA',
    subtitle: 'Descubre cuántos ingresos adicionales y ahorros puede generar HotelStack AI para tu propiedad.',
    roomsLabel: 'Número de Habitaciones',
    adrLabel: 'Tarifa Diaria Promedio',
    occupancyLabel: 'Tasa de Ocupación',
    resultTitle: 'Impacto Anual Estimado',
    revenueLabel: 'Ingresos Adicionales',
    savingsLabel: 'Ahorros Operativos',
    totalLabel: 'Impacto Total Anual',
    perYearSuffix: '/año',
    disclaimer: 'Estimaciones basadas en datos de propiedades beta. Los resultados reales varían.',
  },
  useCases: {
    title: 'IA Para Cada Tipo de Propiedad',
    subtitle: 'Ya sea un hotel boutique o un resort de 500 habitaciones, nuestra IA se adapta.',
    items: [
      { title: 'Hoteles Boutique', description: 'Compite con cadenas usando precios IA y experiencias personalizadas.', stat: '+24%', statLabel: 'Reservas Directas' },
      { title: 'Hoteles de Negocios', description: 'Optimiza tarifas corporativas, automatiza cumplimiento y predice demanda.', stat: '+18%', statLabel: 'Aumento RevPAR' },
      { title: 'Resorts', description: 'Upsells IA, recomendaciones F&B y personalización que aumentan el gasto.', stat: '+32%', statLabel: 'Ingresos Auxiliares' },
      { title: 'Cadenas Hoteleras', description: 'Inteligencia multi-propiedad, gestión centralizada y analítica IA.', stat: '40%', statLabel: 'Menos Trabajo Manual' },
    ],
  },
  faq: {
    title: 'Preguntas Frecuentes sobre IA',
    items: [
      { question: '¿Las 25 funciones de IA están en todos los planes?', answer: 'Las funciones básicas están en todos los planes. Professional añade precios dinámicos. Enterprise desbloquea las 25 funciones completas.' },
      { question: '¿Necesito personal técnico para usar IA?', answer: 'No. Cada función está diseñada para operadores hoteleros. Configuración con clics y modelos pre-entrenados.' },
      { question: '¿Cuándo veré el ROI?', answer: 'La mayoría de propiedades ven mejora en ingresos en 30 días. ROI completo típicamente en 90 días.' },
      { question: '¿Están seguros mis datos?', answer: 'Sí. Todo el procesamiento en entornos cifrados. Cumplimos SOC 2 y GDPR.' },
      { question: '¿Funciona con mis sistemas actuales?', answer: 'Sí. Las funciones IA se integran con tu flujo de trabajo existente sin reemplazar sistemas.' },
      { question: '¿Qué idiomas soporta el concierge IA?', answer: 'El concierge soporta 9 idiomas: inglés, árabe, tailandés, español, francés, alemán, japonés, chino e hindi.' },
    ],
  },
  cta: {
    title: '¿Listo para Transformar Tu Hotel con IA?',
    subtitle: 'Únete a más de 500 propiedades usando HotelStack AI. Prueba gratis 14 días.',
    trialBtn: 'Prueba Gratis',
    demoBtn: 'Reservar Demo',
  },
};

export const AI_FEATURES_CONTENT: LocaleContentMap<AIFeaturesContent> = {
  en,
  ar,
  es,
  ...AI_FEATURES_LOCALES_2,
};
